import { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { AlertCircle, Ban, Check, Clock, Home, Minus, Plus, UserPlus, X } from "lucide-react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import translations from "../../../translations/translations";
import useModalDismiss from "../../common/useModalDismiss";
import "./addBookingPopup.css";
import { CombinedContext } from "../../../context/combinedContext";

/* Duration is the point of this sheet now, so the numbers that drive it
   live at the top rather than inline: the stepper moves on the calendar's
   own 15-minute grain — the same one an empty-slot click snaps to — either
   side of a handful of one-tap presets, with the free window as the
   ceiling. */
const STEP = 15;
const MIN_DURATION = STEP;
const DEFAULT_DURATION = 30;
const QUICK_DURATIONS = [15, 30, 45, 60, 90];

/* Only used when the caller gives us neither a window callback nor a
   positive maxMinutes — i.e. an un-wired caller. Both calendars pass
   one, so this is a floor for safety, not a real code path. */
const FALLBACK_WINDOW = 8 * 60;

/* Start time can move a couple of hours either way, not the one hour the
   popup used to offer, and every option is checked against the calendar
   before it is listed. */
const TIME_SPAN_STEPS = 8;
const TIME_STEP = 15;

const serviceDuration = (svc) => svc?.durationMinutes || svc?.duration || 0;

function serviceName(svc, language) {
	if (language === "es") return svc.serviceNameEs || svc.nameEs || svc.serviceNameEn || svc.nameEn || svc.name || "";
	return svc.serviceNameEn || svc.nameEn || svc.serviceNameEs || svc.nameEs || svc.name || "";
}

function formatDuration(minutes, t) {
	if (minutes < 60) return `${minutes} ${t.minutesShort}`;

	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;

	return rest ? `${hours} ${t.hourShort} ${rest}` : `${hours} ${t.hourShort}`;
}

function addMinutesToHHMM(time, minutes) {
	const [h, m] = time.split(":").map(Number);
	const total = h * 60 + m + minutes;

	const hh = String(Math.floor(total / 60)).padStart(2, "0");
	const mm = String(total % 60).padStart(2, "0");

	return `${hh}:${mm}`;
}

const AddBookingPopup = ({
	staff,
	date,
	startTime,
	services = [],
	maxMinutes,
	/* Optional, and the reason the sheet can be trusted once the start
	   time is changed: the old popup was handed a single maxMinutes
	   measured at the clicked time and kept using it no matter where the
	   user moved the start to. These let it re-ask the calendar. */
	getSlotMaxMinutes,
	getSlotServices,
	isSlotFree,
	token,
	onClose,
	onSuccess,
}) => {
	const { userData, language } = useContext(CombinedContext);

	const t = translations[language].calendar.quickAdd;
	const walkinCopy = translations[language].calendar.walkin;

	const [mode, setMode] = useState("booking"); // booking | block
	const [localStartTime, setLocalStartTime] = useState(startTime);
	const [selectedIds, setSelectedIds] = useState([]);
	const [clientName, setClientName] = useState("");
	const [reason, setReason] = useState("");
	/* null means "follow the services"; any number means the user has
	   taken the wheel and we stop overwriting it. */
	const [duration, setDuration] = useState(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);

	/* Escape closes; a backdrop click does not — this sheet holds a form.
	   Same contract as exclusionModal and walkinModal here. */
	useModalDismiss(onClose);

	// The page behind stays put while the sheet is up.
	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, []);

	/* ───────── what the calendar says about this slot ───────── */

	const slotServices = useMemo(
		() => (getSlotServices ? getSlotServices(localStartTime) : services) || [],
		[getSlotServices, services, localStartTime]
	);

	const windowMinutes = useMemo(() => {
		const raw = getSlotMaxMinutes ? getSlotMaxMinutes(localStartTime) : maxMinutes;

		if (Number.isFinite(raw) && raw > 0) return raw;

		// Trust a callback that reports no room; only guess for callers without one.
		return getSlotMaxMinutes ? 0 : FALLBACK_WINDOW;
	}, [getSlotMaxMinutes, maxMinutes, localStartTime]);

	const noRoom = windowMinutes < MIN_DURATION;
	const cap = Math.max(MIN_DURATION, windowMinutes);

	const start = dayjs(`${date}T${localStartTime}`);
	const windowEnd = start.add(windowMinutes, "minute");

	/* Derived from slotServices rather than stored, so anything that stops
	   fitting when the start time moves drops out on its own. */
	const selectedServices = useMemo(
		() => slotServices.filter((s) => selectedIds.includes(s.serviceId)),
		[slotServices, selectedIds]
	);

	const serviceTotal = selectedServices.reduce((sum, s) => sum + serviceDuration(s), 0);

	const autoDuration = mode === "booking" && serviceTotal > 0 ? serviceTotal : DEFAULT_DURATION;

	/* A service books its own length exactly — a 20-minute service is 20
	   minutes, not rounded up onto the grid. The 15-minute floor is for
	   durations the user set by hand. */
	const durationFloor = duration === null ? 1 : MIN_DURATION;

	const finalDuration = Math.min(Math.max(duration ?? autoDuration, durationFloor), cap);

	const end = start.add(finalDuration, "minute");

	const remainingMinutes = windowMinutes - serviceTotal;

	/* Presets, trimmed to what actually fits, plus the whole window as a
	   last chip so "block until the next appointment" is one tap. */
	const durationChips = useMemo(() => {
		const set = new Set(QUICK_DURATIONS.filter((m) => m <= cap));
		set.add(cap);

		return [...set].sort((a, b) => a - b);
	}, [cap]);

	const timeOptions = useMemo(() => {
		const base = dayjs(`${date}T${startTime}`);
		const found = [];

		for (let i = -TIME_SPAN_STEPS; i <= TIME_SPAN_STEPS; i++) {
			const candidate = base.add(i * TIME_STEP, "minute");

			// Never roll into the day before or after — the payload is date-bound.
			if (!candidate.isSame(base, "day")) continue;

			const hhmm = candidate.format("HH:mm");

			// The clicked time is always offered; the rest have to earn it.
			if (hhmm !== startTime) {
				if (isSlotFree && !isSlotFree(hhmm)) continue;

				if (getSlotMaxMinutes) {
					const room = getSlotMaxMinutes(hhmm);
					if (!Number.isFinite(room) || room < MIN_DURATION) continue;
				}
			}

			found.push(hhmm);
		}

		if (!found.includes(localStartTime)) found.push(localStartTime);

		return found.sort();
	}, [date, startTime, localStartTime, isSlotFree, getSlotMaxMinutes]);

	/* ───────── service rules ───────── */

	const hasHomeService = selectedServices.some((s) => s.isHomeService === true);
	const hasInShopService = selectedServices.some((s) => s.isHomeService !== true);

	const lockedType = hasHomeService ? "home" : hasInShopService ? "shop" : null;

	function canAddService(service) {
		if (!lockedType) return true;
		if (lockedType === "home") return service.isHomeService === true;
		return service.isHomeService !== true;
	}

	function toggleService(service) {
		setError(null);

		setSelectedIds((prev) =>
			prev.includes(service.serviceId) ? prev.filter((id) => id !== service.serviceId) : [...prev, service.serviceId]
		);
	}

	function nudgeDuration(delta) {
		setError(null);

		// Snap onto the 15-minute grid first, so a 20-minute service steps to 30, not 35.
		const next = Math.round((finalDuration + delta) / STEP) * STEP;

		setDuration(Math.min(Math.max(next, MIN_DURATION), cap));
	}

	/* ───────── save ───────── */

	const canSubmit = !saving && !noRoom && (mode === "booking" ? selectedServices.length > 0 : true);

	async function handleConfirm() {
		if (!canSubmit) return;

		setError(null);
		setSaving(true);

		try {
			if (mode === "booking") {
				await companyBasedRequest.manualBooking(
					{
						shopId: userData.user.shopId,

						staffId: staff.staffId,
						staffName: `${staff.firstName || ""} ${staff.lastName || ""}`.trim(),

						startTime: start.format("YYYY-MM-DDTHH:mm:ss"),
						endTime: end.format("YYYY-MM-DDTHH:mm:ss"),

						services: selectedServices.map((s) => ({
							serviceId: s.serviceId,
							serviceNameEn: s.serviceNameEn || s.nameEn || s.name,
							serviceNameEs: s.serviceNameEs || s.nameEs || s.name,
							duration: serviceDuration(s),
							isHomeService: s.isHomeService === true,
						})),

						customerName: clientName.trim() || "Walk-In",
						notes: null,
					},
					token
				);
			} else {
				await scheduleBasedRequest.addScheduleExclusion(
					staff.scheduleId,
					{
						startDate: date,
						endDate: date,
						reason: reason.trim() || translations[language].calendar.exclusion.reasonDefault,
						blocks: [{ start: localStartTime, end: addMinutesToHHMM(localStartTime, finalDuration) }],
					},
					token
				);
			}

			onSuccess();
			onClose();
		} catch (err) {
			// Was a bare try/finally: the request could fail and the sheet
			// would just sit there looking idle. Say so instead.
			setError(mode === "booking" ? t.errorWalkIn : t.errorBlock);
		} finally {
			setSaving(false);
		}
	}

	const staffLabel = [staff?.firstName, staff?.lastName].filter(Boolean).join(" ");

	const showMatchServices = mode === "booking" && serviceTotal > 0 && duration !== null && duration !== serviceTotal;

	return (
		<div className="sf-qa-overlay" role="presentation">
			<div
				className="sf-qa-sheet"
				role="dialog"
				aria-modal="true"
				aria-label={mode === "booking" ? t.titleWalkIn : t.titleBlock}
			>
				<span className="sf-qa-grabber" aria-hidden="true" />

				{/* ───────── HEADER ───────── */}
				<header className="sf-qa-header">
					<div className="sf-qa-heading">
						<h3>{mode === "booking" ? t.titleWalkIn : t.titleBlock}</h3>
						<p>
							{start.format("ddd, MMM D")}
							{staffLabel && <span className="sf-qa-dot"> · {staffLabel}</span>}
						</p>
					</div>

					<button className="sf-qa-close" onClick={onClose} aria-label={t.cancel}>
						<X size={17} strokeWidth={2.4} />
					</button>
				</header>

				{/* ───────── MODE ───────── */}
				<div className="sf-qa-tabs" role="tablist">
					<button
						role="tab"
						type="button"
						aria-selected={mode === "booking"}
						className={`sf-qa-tab ${mode === "booking" ? "active" : ""}`}
						onClick={() => {
							setMode("booking");
							setError(null);
						}}
					>
						<UserPlus size={14} strokeWidth={2.4} />
						{t.walkIn}
					</button>

					<button
						role="tab"
						type="button"
						aria-selected={mode === "block"}
						className={`sf-qa-tab ${mode === "block" ? "active" : ""}`}
						onClick={() => {
							setMode("block");
							setError(null);
						}}
					>
						<Ban size={14} strokeWidth={2.4} />
						{t.blockTime}
					</button>
				</div>

				<div className="sf-qa-body">
					{/* ───────── START ───────── */}
					<section className="sf-qa-field">
						<label htmlFor="sf-qa-start">{t.startsAt}</label>

						<div className="sf-qa-select-wrap">
							<Clock size={15} strokeWidth={2.2} />

							<select
								id="sf-qa-start"
								className="sf-qa-select"
								value={localStartTime}
								onChange={(e) => {
									setLocalStartTime(e.target.value);
									setError(null);
								}}
							>
								{timeOptions.map((time) => (
									<option key={time} value={time}>
										{dayjs(`${date}T${time}`).format("h:mm A")}
									</option>
								))}
							</select>
						</div>
					</section>

					{/* ───────── DURATION ───────── */}
					<section className="sf-qa-field">
						<div className="sf-qa-field-head">
							<label>{t.duration}</label>

							{showMatchServices && (
								<button type="button" className="sf-qa-link" onClick={() => setDuration(null)}>
									{t.matchServices}
								</button>
							)}
						</div>

						<div className="sf-qa-stepper">
							<button
								type="button"
								aria-label={t.less}
								disabled={noRoom || finalDuration <= MIN_DURATION}
								onClick={() => nudgeDuration(-STEP)}
							>
								<Minus size={16} strokeWidth={2.6} />
							</button>

							<div className="sf-qa-stepper-read">
								<strong>{formatDuration(finalDuration, t)}</strong>
								<span>
									{start.format("h:mm A")} – {end.format("h:mm A")}
								</span>
							</div>

							<button
								type="button"
								aria-label={t.more}
								disabled={noRoom || finalDuration >= cap}
								onClick={() => nudgeDuration(STEP)}
							>
								<Plus size={16} strokeWidth={2.6} />
							</button>
						</div>

						<div className="sf-qa-chips">
							{durationChips.map((minutes) => (
								<button
									key={minutes}
									type="button"
									className={`sf-qa-chip ${finalDuration === minutes ? "is-active" : ""} ${
										minutes === cap ? "is-max" : ""
									}`}
									disabled={noRoom}
									onClick={() => {
										setDuration(minutes);
										setError(null);
									}}
								>
									{formatDuration(minutes, t)}
									{minutes === cap && <span className="sf-qa-chip-max">{t.max}</span>}
								</button>
							))}
						</div>

						<p className={`sf-qa-hint ${noRoom ? "is-warn" : ""}`}>
							{noRoom
								? t.noRoom
								: t.freeWindow
										.replace("{minutes}", formatDuration(windowMinutes, t))
										.replace("{time}", windowEnd.format("h:mm A"))}
						</p>
					</section>

					{/* ───────── WALK-IN ───────── */}
					{mode === "booking" && (
						<>
							<section className="sf-qa-field">
								<div className="sf-qa-field-head">
									<label>{t.services}</label>

									{selectedServices.length > 0 && (
										<span className="sf-qa-count">
											{selectedServices.length} · {formatDuration(serviceTotal, t)}
										</span>
									)}
								</div>

								{slotServices.length === 0 ? (
									<p className="sf-qa-empty">{walkinCopy.service.none}</p>
								) : (
									<div className="sf-qa-services">
										{slotServices.map((svc) => {
											const isSelected = selectedIds.includes(svc.serviceId);
											const minutes = serviceDuration(svc);

											const exceedsWindow = !isSelected && minutes > remainingMinutes;
											const disabled = noRoom || !canAddService(svc) || exceedsWindow;

											return (
												<button
													key={svc.serviceId}
													type="button"
													className={`sf-qa-service ${isSelected ? "is-selected" : ""}`}
													disabled={disabled}
													onClick={() => toggleService(svc)}
												>
													<span className="sf-qa-service-check" aria-hidden="true">
														{isSelected && <Check size={12} strokeWidth={3.2} />}
													</span>

													<span className="sf-qa-service-name">
														{serviceName(svc, language)}

														{svc.isHomeService && (
															<span className="sf-qa-tag">
																<Home size={10} strokeWidth={2.6} />
																{t.homeService}
															</span>
														)}
													</span>

													<span className="sf-qa-service-dur">{formatDuration(minutes, t)}</span>
												</button>
											);
										})}
									</div>
								)}
							</section>

							<section className="sf-qa-field">
								<label htmlFor="sf-qa-client">{t.client}</label>

								<input
									id="sf-qa-client"
									type="text"
									className="sf-qa-input"
									placeholder={walkinCopy.client.placeholder}
									value={clientName}
									onChange={(e) => setClientName(e.target.value)}
								/>
							</section>
						</>
					)}

					{/* ───────── BLOCK ───────── */}
					{mode === "block" && (
						<section className="sf-qa-field">
							<label htmlFor="sf-qa-reason">{t.reason}</label>

							<input
								id="sf-qa-reason"
								type="text"
								className="sf-qa-input"
								placeholder={t.reasonPlaceholder}
								value={reason}
								onChange={(e) => setReason(e.target.value)}
							/>
						</section>
					)}

					{error && (
						<div className="sf-qa-error" role="alert">
							<AlertCircle size={15} strokeWidth={2.4} />
							{error}
						</div>
					)}
				</div>

				{/* ───────── FOOTER ───────── */}
				<footer className="sf-qa-footer">
					<button type="button" className="sf-qa-btn ghost" onClick={onClose} disabled={saving}>
						{t.cancel}
					</button>

					<button type="button" className="sf-qa-btn primary" disabled={!canSubmit} onClick={handleConfirm}>
						{saving ? (
							<span className="sf-qa-spinner" aria-label={t.saving} />
						) : mode === "booking" ? (
							t.confirmWalkIn
						) : (
							t.confirmBlock
						)}
					</button>
				</footer>
			</div>
		</div>
	);
};

export default AddBookingPopup;
